import { Component, Suspense, lazy, createRef } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import classNames from 'classnames';

import FolderIcon from 'dibs-icons/exports/Folder';
import HeartIcon from 'dibs-icons/exports/HeartFilled';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';

import { HeartWrapperLazy } from '../../../global/HeartWrapperLazy';
import { FolderWrapperLazy } from '../../../global/FolderWrapperLazy';
import {
    locations,
    authLabels,
    folderTrackingHooks,
    trackHeartFavorited,
    trackHeartUnfavorited,
} from '../../../utils/tracking/shared/favoritesTracking';
import { loadAddToFolderModalWrapper } from '../../../global/loadAddToFolderModalWrapper';
import {
    SAVE_ITEM_FLOW,
    SAVE_ITEM_TO_FOLDER_FLOW,
    SAVE_AUCTION_ITEM_FLOW,
    SAVE_AUCTION_ITEM_TO_FOLDER_FLOW,
    sharedLoginCheck,
} from '../../../authentication/sharedLoginCheck';
import {
    FORCE_FAVORITE_ITEM_ID,
    FORCE_FOLDER_ITEM_ID,
    setForceFavoriteItemId,
    getForceFavoriteItemId,
    clearForceFavoriteItemId,
} from './localStorageHelpers';

import styles from './FavoriteIcons.scss';

const FavoritesTooltipLazy = lazy(() =>
    import(/* webpackChunkName: "FavoritesTooltip" */ './FavoritesTooltip')
);

const AuctionsFavoritesTooltipLazy = lazy(() =>
    import(/* webpackChunkName: "AuctionsFavoritesTooltip" */ './AuctionsTooltip')
);

export class FavoritesItemComponent extends Component {
    constructor(props) {
        super(props);

        this.showAuthModal = this.showAuthModal.bind(this);
        this.getFolderModalComponent = this.getFolderModalComponent.bind(this);
        this.onAddItemSuccess = this.onAddItemSuccess.bind(this);
        this.onFavorited = this.onFavorited.bind(this);
        this.onUnFavorited = this.onUnFavorited.bind(this);

        this.folderTrackingHooks = this.getFolderTrackingHooks(props);

        this.favoritesRef = createRef();

        this.state = {
            openAddToFolderModal: false,
        };
    }

    componentDidMount() {
        const shouldOpenAddToFolderModal =
            getForceFavoriteItemId(FORCE_FOLDER_ITEM_ID) === this.props.item?.serviceId;

        if (shouldOpenAddToFolderModal) {
            this.setState({ openAddToFolderModal: true });
            clearForceFavoriteItemId(FORCE_FOLDER_ITEM_ID);
        }
    }

    onAddItemSuccess() {
        if (authModalLoader.isPaused()) {
            authModalLoader.resume();
        }

        if (this.props.onFolderActionEnd) {
            this.props.onFolderActionEnd();
        }
    }

    getFolderTrackingHooks(props) {
        let hooks = folderTrackingHooks;

        if (props.location) {
            hooks = Object.keys(folderTrackingHooks).reduce((map, current) => {
                map[current] = folderTrackingHooks[current].bind(
                    null,
                    props.item?.isAuctionEnabled ? locations.AUCTION_PDP : props.location
                );
                return map;
            }, {});
        }

        return hooks;
    }

    showAuthModal({ flow, onSuccess, onError, shouldPauseAuthFlow } = {}) {
        const { itemImage, item } = this.props;
        sharedLoginCheck({
            onSuccess,
            onError,
            authOptions: {
                flow,
                shouldPauseAuthFlow,
                ga: { label: authLabels[this.props.location] },
                additionalProps: { item: { firstPhotoWebPath: itemImage, title: item?.title } },
            },
        });
    }

    getFolderModalComponent() {
        const flow = this.props.item?.isAuctionEnabled
            ? SAVE_AUCTION_ITEM_TO_FOLDER_FLOW
            : SAVE_ITEM_TO_FOLDER_FLOW;

        return new Promise((resolve, reject) => {
            this.showAuthModal({
                flow,
                shouldPauseAuthFlow: state => state.step === 0,
                onSuccess: ({ userId, isVerifiedTrade }) => {
                    if (isVerifiedTrade) {
                        // after trade user logs in, page is reloaded, so there's no point in showing modal
                        setForceFavoriteItemId(FORCE_FOLDER_ITEM_ID, this.props.item?.serviceId);
                        return resolve();
                    }
                    return loadAddToFolderModalWrapper({ userId }).then(resolve);
                },
                onError: reject,
            });
        });
    }

    renderHeart() {
        const { disable, item, userId, viewer, heartIconClass, setHeartFilledStatus } = this.props;

        if (disable) {
            return <HeartIcon className={styles.icon} />;
        }

        const flow = item?.isAuctionEnabled ? SAVE_AUCTION_ITEM_FLOW : SAVE_ITEM_FLOW;

        return (
            <HeartWrapperLazy
                load
                itemId={item?.serviceId}
                userId={userId}
                viewer={viewer}
                authModalShow={cb =>
                    this.showAuthModal({
                        flow,
                        onSuccess: ({ isVerifiedTrade }) => {
                            if (isVerifiedTrade) {
                                setForceFavoriteItemId(FORCE_FAVORITE_ITEM_ID, item?.serviceId);
                            }
                            if (cb && typeof cb === 'function') {
                                cb();
                            }
                        },
                    })
                }
                onFavorited={this.onFavorited}
                onUnFavorited={this.onUnFavorited}
                className={heartIconClass}
                buttonClass={styles.heartButton}
                theme="dark"
                setHeartFilledStatus={setHeartFilledStatus}
            />
        );
    }

    onUnFavorited() {
        const { item, location, onUnFavorited } = this.props;
        onUnFavorited();
        trackHeartUnfavorited(
            item?.isAuctionEnabled ? locations.AUCTION_PDP : location,
            item?.serviceId
        );
    }

    onFavorited() {
        const { item, location, onFavorited } = this.props;
        onFavorited();
        trackHeartFavorited(
            item?.isAuctionEnabled ? locations.AUCTION_PDP : location,
            item?.serviceId
        );
    }

    renderFolder() {
        const {
            disable,
            item,
            userId,
            userIds,
            itemIds,
            viewer,
            singlePortfolioItem,
            loadPortfolioItemData,
            environment,
            folderIconClass,
            showFolderPlusIcon,
            onFolderActionStart,
            onFolderActionEnd,
        } = this.props;

        if (disable) {
            return <FolderIcon className={styles.icon} />;
        }

        return (
            <FolderWrapperLazy
                load
                userId={userId}
                userIds={userIds}
                primaryItemPk={item?.serviceId}
                selectedItemIds={itemIds}
                singlePortfolioItem={singlePortfolioItem}
                loadPortfolioItemData={loadPortfolioItemData}
                viewer={viewer}
                environment={environment}
                trackingHooks={this.folderTrackingHooks}
                getModalComponent={this.getFolderModalComponent}
                additionalIconClass={folderIconClass}
                onAddItemSuccess={this.onAddItemSuccess}
                shouldFillInFolder={!!userId}
                openOnRender={this.state.openAddToFolderModal}
                theme="dark"
                showPlusIcon={showFolderPlusIcon}
                onFolderSave={onFolderActionStart}
                onFolderRemove={onFolderActionStart}
                onNewFolderSave={onFolderActionStart}
                onRemoveItemSuccess={onFolderActionEnd}
            />
        );
    }

    render() {
        const {
            fetchHeart,
            fetchFolder,
            heartIconWrapperClass,
            folderIconWrapperClass,
            wrapperClass,
            disable,
            showTooltip,
            showAuctionsTooltip,
        } = this.props;

        if (disable) {
            return null;
        }

        return (
            <div className={classNames(styles.wrap, wrapperClass)} ref={this.favoritesRef}>
                {showTooltip && (
                    <Suspense fallback="">
                        <FavoritesTooltipLazy elementRef={this.favoritesRef} />
                    </Suspense>
                )}
                {showAuctionsTooltip && (
                    <Suspense fallback="">
                        <AuctionsFavoritesTooltipLazy />
                    </Suspense>
                )}

                <Suspense fallback="">
                    {fetchHeart && (
                        <div
                            className={classNames(styles.iconWrap, {
                                [heartIconWrapperClass]: !!heartIconWrapperClass,
                            })}
                            data-tn="item-save-to-portfolio"
                        >
                            {this.renderHeart()}
                        </div>
                    )}
                    {fetchFolder && (
                        <div
                            className={classNames(styles.iconWrap, {
                                [folderIconWrapperClass]: !!folderIconWrapperClass,
                            })}
                            data-tn="item-save-to-folder"
                        >
                            {this.renderFolder()}
                        </div>
                    )}
                </Suspense>
            </div>
        );
    }
}

FavoritesItemComponent.defaultProps = {
    fetchHeart: true,
    fetchFolder: true,
    onFavorited: () => {},
    onUnFavorited: () => {},
};

FavoritesItemComponent.propTypes = {
    itemImage: PropTypes.string,
    location: PropTypes.oneOf([
        locations.SIDEBAR,
        locations.SELLER_GRID,
        locations.SIMILAR_ITEM_GRID,
        locations.SIMILAR_ITEM_TOP,
        locations.VISUALLY_SIMILAR_ITEM_GRID,
        locations.SEARCH_BROWSE,
        locations.ZOOM_MODAL,
        locations.VIEW_IN_ROOM,
        locations.AUCTION_PDP,
    ]).isRequired,

    // passed from FavoritesProvider
    disable: PropTypes.bool,
    isVerifiedTrade: PropTypes.bool,
    userId: PropTypes.string,
    userIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    itemIds: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    environment: PropTypes.object.isRequired,
    singlePortfolioItem: PropTypes.bool.isRequired,
    loadPortfolioItemData: PropTypes.bool.isRequired,
    viewer: PropTypes.object,
    fetchHeart: PropTypes.bool.isRequired,
    fetchFolder: PropTypes.bool.isRequired,
    heartIconClass: PropTypes.string,
    heartIconWrapperClass: PropTypes.string,
    folderIconClass: PropTypes.string,
    folderIconWrapperClass: PropTypes.string,
    wrapperClass: PropTypes.string,
    item: PropTypes.object.isRequired,
    showFolderPlusIcon: PropTypes.bool,
    showTooltip: PropTypes.bool,
    showAuctionsTooltip: PropTypes.bool,
    onFavorited: PropTypes.func,
    onUnFavorited: PropTypes.func,
    onFolderActionStart: PropTypes.func,
    onFolderActionEnd: PropTypes.func,
    setHeartFilledStatus: PropTypes.func,
};

export const FavoritesItem = createFragmentContainer(FavoritesItemComponent, {
    item: graphql`
        fragment FavoritesItem_item on Item {
            serviceId
            title
            isAuctionEnabled
        }
    `,
});
